<template>
  <div class="rs-avatar-dropdown">
    <div class="mx-2 p-2">
      <div class="dropdown-avatar-header py-2">
        <div class="title">
          {{ user.firstName }} {{ user.lastName }}
        </div>
        <div class="meta">
          {{ user.email }}
        </div>
      </div>
      <div class="border-top py-3">
        <label class="d-block text-uppercase text-muted mb-0">ACCOUNT</label>
        <RsDropdownItem
          title="My Profile"
          icon="user-edit"
          @click="handleUserProfileClicked"
        />
        <RsDropdownItem
          title="Logout"
          icon="sign-out-alt"
          @click="handleLogoutClicked"
        />
        <RsDropdownItem
          v-if="isImpersonating"
          title="Stop Impersonating"
          icon="user-minus"
          @click="handleStopImpersonatingClicked"
        />
      </div>

      <div class="border-top py-3">
        <label class="d-block text-uppercase text-muted mb-0">CLIENT</label>
        <div>
          <RsPartialNavCompanySettings class="w-100 pb-2" />
          <div class="border-top py-2" />
          <RsPolicies
            font-size="14px"
            :show-full-policies="true"
          />
        </div>
      </div>

      <div v-if="user.isSuperUser" class="border-top py-3">
        <label class="d-block text-uppercase text-muted">CHANGE CLIENT</label>
        <RsFormInput
          ref="clientSearchBox"
          v-model="clientSearchValue"
          type="text"
          :debounce="300"
          icon="search"
          placeholder="Search by id, key, or name"
        />

        <div class="row mx-n1 rs-text-black">
          <div
            v-for="client in clients"
            :key="client.id ?? 0"
            class="col-6 px-1 pb-2"
          >
            <div
              class="border text-truncate p-2 rounded cursor-pointer client-pill"
              :title="`${client.lwsCompanyLabel || client.name}\nID: ${client.id}\nClient Key: ${client.clientKey}`"
              @click="handleClientClicked(client)"
            >
              <div class="d-flex align-items-center">
                <i :class="getClientIconClass(client)" />
                <div class="pl-2 text-truncate">
                  {{ client.lwsCompanyLabel || client.name }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!user.isSuperUser && clients.length" class="border-top py-3">
        <label class="d-block text-uppercase text-muted">CHANGE CLIENT</label>
        <div class="row rs-text-black">
          <div
            v-for="client in clients"
            :key="client.id ?? 0"
            class="col-12"
          >
            <div
              class="border text-truncate p-2 rounded cursor-pointer client-pill"
              @click="handleClientClicked(client)"
            >
              {{ client.name }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <RsModalAccount v-model="isUserModalOpen" />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IClients } from '@llws/lift-entity-interfaces';
import RsPartialNavCompanySettings from '@/components/RsPartialNavCompanySettings.vue';
import RsModalAccount from '@/components/RsModalAccount.vue';
import { users } from '@/store/typed/typed-modules';
import userService from '@/shared/services/users';
import { mapModuleActions, mapModuleGetters } from '@/shared/vuex-toolkit';
import { storage } from '@/shared/helpers/storage';

export default defineComponent({
  name: 'RsPartialAvatarDropdown',

  components: {
    RsPartialNavCompanySettings,
    RsModalAccount,
  },

  props: {
    visibilityWatch: {
      type: Number,
      default: 0,
    },
  },

  data() {
    return {
      clientSearchValue: '',
      isUserModalOpen: false as boolean,
    };
  },

  computed: {
    ...mapModuleGetters(users, { clients: 'accessibleClients' }),

    user() {
      return this.$rsGlobals.$currentUser;
    },

    isImpersonating(): boolean {
      return storage.get('currentUserSettings')?.impersonated;
    },

    clientSearchInput(): HTMLInputElement | null {
      return (this.$refs.clientSearchBox as Vue)?.$el.querySelector('input');
    },
  },

  watch: {
    visibilityWatch() {
      this.handleShow();
    },

    clientSearchValue() {
      this.getClients(this.clientSearchValue);
    },
  },

  methods: {
    ...mapModuleActions(users, {
      getClients: 'getAccessibleClients',
      changeClient: 'changeCurrentClient',
    }),

    handleShow() {
      this.clientSearchValue = '';
      this.getClients(this.clientSearchValue);

      setTimeout(() => {
        if (this.clientSearchInput) {
          this.clientSearchInput.focus();
        }
      }, 300);
    },

    handleLogoutClicked() {
      userService.logout();
    },

    async handleStopImpersonatingClicked() {
      await userService.stopImpersonating();
      window.location.reload();
    },

    handleUserProfileClicked() {
      this.isUserModalOpen = true;
    },

    getClientIconClass(client: IClients& { isBundleClient?: boolean }): Record<string, boolean> {
      return {
        'd-block': true,
        'far fa-gift': !!client.serviceWebsite && !!client.serviceRentsync,
        'far fa-desktop': !!client.serviceWebsite && !client.serviceRentsync,
        'far fa-sync-alt': !!client.serviceRentsync && !client.serviceWebsite,
        'rs-text-rentals': !!client.isBundleClient,
      };
    },

    async handleClientClicked(client: IClients) {
      await this.changeClient(client.id ?? 0);

      if (Object.keys(this.$route.params || {}).length > 0) {
        window.location.replace('/');
        return;
      }

      window.location.replace(this.$route.path);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
$dropdown-text-color: #0a0a0a;
.rs-avatar-dropdown {
  .nav-link {
    color: $black !important;

    i {
      color: inherit !important;
    }
  }

  .dropdown-avatar-header {
    font-size: 12px;
    .title {
      font-size: 1rem;
      line-height: 1.2rem;
      font-weight: 700;
      color: $dropdown-text-color;
    }
    .meta {
      line-height: 1.2rem;
      font-size: 14px;
      color: $dropdown-text-color;
    }
  }

  .client-pill {
    font-size: 14px;
    font-weight: bold;

    &:hover {
      background: $tertiary-light-purple;
    }
  }
}

</style>
